import React, { useState } from 'react';
import { PageShell } from './PageShell';
import { LoadingSkeleton } from './LoadingSkeleton';
import { EmptyState } from './EmptyState';
import { StandardReadView } from './StandardReadView';
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
    Pagination,
    PaginationContent,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
} from "@/components/ui/pagination";

/**
 * StandardCollectionPage
 * 
 * Authority: Universal Read-View Contract (READ_VIEW_LAW.md)
 * Enforcement: RV-CLASS-A Prevention
 * 
 * The Single Source of Truth for rendering lists of items.
 * Enforces: grid layout, empty states, pagination, header structure,
 * and AUTOMATIC click-to-detail-view binding.
 * 
 * All list items are automatically clickable and open StandardReadView.
 */
export function StandardCollectionPage({
    config = { title: 'Collection', description: '' },
    schema = null, // NEW: Required for detail view (RV-CLASS-A enforcement)
    data = [],
    status = 'loading', // 'loading' | 'success' | 'error'
    filters = null,
    actions = null,
    renderCard,
    pagination = null, // { page, totalPages, onPageChange }
    onRefresh,
    onItemClick = null, // NEW: Optional custom click handler (defaults to StandardReadView)
    schemaAdapter = null // NEW: Optional adapter function to transform data for schema
}) {
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedItem, setSelectedItem] = useState(null); // NEW: Selected item for detail view
    const ITEMS_PER_PAGE = 12;

    // -- Derived State (if no server pagination provided) --
    const isServerPagination = !!pagination;
    const activePage = isServerPagination ? pagination.page : currentPage;
    const totalPages = isServerPagination
        ? pagination.totalPages
        : Math.ceil((data?.length || 0) / ITEMS_PER_PAGE);

    const paginatedData = isServerPagination
        ? data
        : (data || []).slice((activePage - 1) * ITEMS_PER_PAGE, activePage * ITEMS_PER_PAGE);

    // -- Universal Click Handler (RV-CLASS-A Prevention) --
    const handleItemClick = (item) => {
        if (onItemClick) {
            // Custom handler provided
            onItemClick(item);
        } else if (schema) {
            // Default: Open StandardReadView
            const adaptedItem = schemaAdapter ? schemaAdapter(item) : item;
            setSelectedItem(adaptedItem);
        }
        // If no schema and no custom handler, do nothing (but this violates RV-CLASS-A)
    };

    // -- Render Helpers --

    const renderContent = () => {
        if (status === 'loading') {
            return (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                        <div key={i} className="h-[260px] bg-muted/30 rounded-lg animate-pulse border border-muted" /* visual-law-ignore */ />
                    ))}
                </div>
            );
        }

        if (status === 'error') {
            return (
                <div className="bg-muted/40 border border-border rounded-lg p-8 text-center">
                    <h2 className="text-xl font-bold text-foreground mb-2">Unable to Load Items</h2>
                    <p className="text-base text-muted-foreground mb-4">We could not retrieve this data right now. This may be temporary.</p>
                    {onRefresh && (
                        <Button onClick={onRefresh}>
                            Try Again
                        </Button>
                    )}
                </div>
            );
        }

        if (!data || data.length === 0) {
            return (
                <EmptyState
                    title={config.emptyTitle || `No ${config.entityName || 'items'} found`}
                    description={config.emptyDescription || "Your collection is empty."}
                    icon={config.emptyIcon || null}
                    action={config.emptyAction || null}
                />
            );
        }

        return (
            <div className="space-y-8">
                {/* Grid Layout Enforced */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="collection-grid">
                    {(paginatedData || []).map((item, idx) => (
                        <div
                            key={item.id || item._id || idx}
                            className="h-full cursor-pointer"
                            onClick={() => handleItemClick(item)}
                            data-testid="collection-item"
                        >
                            {renderCard(item)}
                        </div>
                    ))}
                </div>

                {/* Standard Pagination */}
                {totalPages > 1 && (
                    <Pagination>
                        <PaginationContent>
                            <PaginationItem>
                                <PaginationPrevious
                                    href="#"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        if (isServerPagination) pagination.onPageChange(Math.max(1, activePage - 1));
                                        else setCurrentPage(p => Math.max(1, p - 1));
                                    }}
                                    className={activePage <= 1 ? "pointer-events-none opacity-50" : ""}
                                />
                            </PaginationItem>

                            <span className="text-sm text-muted-foreground px-4">
                                Page {activePage} of {totalPages}
                            </span>

                            <PaginationItem>
                                <PaginationNext
                                    href="#"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        if (isServerPagination) pagination.onPageChange(Math.min(totalPages, activePage + 1));
                                        else setCurrentPage(p => Math.min(totalPages, p + 1));
                                    }}
                                    className={activePage >= totalPages ? "pointer-events-none opacity-50" : ""}
                                />
                            </PaginationItem>
                        </PaginationContent>
                    </Pagination>
                )}
            </div>
        );
    };

    return (
        <>
            <PageShell
                title={config.title}
                description={config.description}
                actions={actions}
                filterBar={filters}
            >
                {renderContent()}
            </PageShell>

            {/* Auto-rendered Detail View (RV-CLASS-A Prevention) */}
            {selectedItem && schema && (
                <StandardReadView
                    schema={schema}
                    data={selectedItem}
                    onClose={() => setSelectedItem(null)}
                />
            )}
        </>
    );
}
